import { useMemo, useId } from 'react';
import { useApp } from '../contexts/AppContext';
import { useTr } from '../utils/useTr';

/**
 * BeeloveLogo — inline SVG, transparent background, animated EKG line.
 *
 * Props:
 *   variant  : 'compact' | 'full'   (default: 'compact')
 *   theme    : 'dark'    | 'light'  (default: 'dark')
 *   height   : number               (default: 36 for compact, 72 for full)
 *   animate  : bool                 (default: true — EKG draws on mount)
 *   tagline  : bool                 (default: false — show tr('logo.tagline'))
 */

// ── EKG path generator — pure function, no Hooks ───────────────
function buildEkg(x0, sigY, totalW, scale = 1) {
  const pts = [];
  let ox = x0;
  const seg = (n, fn) => {
    for (let i = 0; i < n; i++) pts.push([+(ox + i).toFixed(1), +(sigY + fn(i / Math.max(n - 1, 1))).toFixed(1)]);
    ox += n;
  };
  const pi = Math.PI;
  const sin = Math.sin;
  const s = scale;
  const w = totalW;
  seg(~~(w * .13),  () => 0);
  seg(~~(w * .04),  t => -sin(pi * t) * 16 * s);
  seg(~~(w * .04),  () => 0);
  seg(~~(w * .02),  t =>  sin(pi * t) * 11 * s);
  seg(~~(w * .045), t => -sin(pi * t) * 78 * s);
  seg(~~(w * .025), t =>  sin(pi * t) * 24 * s);
  seg(~~(w * .03),  () => 0);
  seg(~~(w * .08),  t => -sin(pi * t) * 32 * s);
  seg(~~(w * .07),  () => 0);
  seg(~~(w * .03),  t => -sin(pi * t) * 11 * s);
  seg(~~(w * .025), () => 0);
  seg(~~(w * .015), t =>  sin(pi * t) * 9  * s);
  seg(~~(w * .03),  t => -sin(pi * t) * 50 * s);
  seg(~~(w * .02),  t =>  sin(pi * t) * 17 * s);
  seg(~~(w * .025), () => 0);
  seg(~~(w * .055), t => -sin(pi * t) * 20 * s);
  const rem = Math.max(0, x0 + totalW - ox);
  seg(rem, t => sin(t * pi * 5) * 6 * s * (1 - t));
  return pts;
}

function pathD(pts) {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
}

function pathLen(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0];
    const dy = pts[i][1] - pts[i - 1][1];
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return Math.ceil(len);
}

// ── Component ──────────────────────────────────────────────────────
export default function BeeloveLogo({
  variant  = 'compact',
  height,
  animate  = true,
  tagline  = false,
  themeOverride,
}) {
  // ALL hooks at the top — before any conditionals
  const { theme: ctxTheme } = useApp();
  const tr  = useTr();
  const uid = useId().replace(/:/g, '_');

  const theme  = themeOverride || ctxTheme || 'dark';
  const isDark = theme === 'dark';

  // Colors
  const textMain  = isDark ? '#F0EAD8' : '#16100A';
  const goldStart = isDark ? '#E2C06E' : '#A07010';
  const goldMid   = isDark ? '#C9A84C' : '#8A5E08';
  const goldEnd   = isDark ? '#A8882C' : '#6A4606';
  const muted     = isDark ? '#55504A' : '#9A8860';
  const goldSolid = isDark ? '#C9A84C' : '#9B6E10';

  // Pre-compute both variants unconditionally (cheap, memoized)
  const ptsCompact = useMemo(() => buildEkg(0, 52,  300, 0.82), []);
  const ptsFull    = useMemo(() => buildEkg(0, 82,  640, 1.0),  []);
  const dCompact   = useMemo(() => pathD(ptsCompact),   [ptsCompact]);
  const dFull      = useMemo(() => pathD(ptsFull),      [ptsFull]);
  const lenCompact = useMemo(() => pathLen(ptsCompact), [ptsCompact]);
  const lenFull    = useMemo(() => pathLen(ptsFull),    [ptsFull]);

  // ── Compact ──
  if (variant === 'compact') {
    const VW = 300, VH = 62;
    const h  = height ?? 38;
    const animStyle = animate ? {
      strokeDasharray:  lenCompact,
      strokeDashoffset: lenCompact,
      animation: `drawEkg_${uid} 2s cubic-bezier(.4,0,.2,1) forwards`,
      animationDelay: '0.2s',
    } : { strokeDasharray: 'none' };

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${VW} ${VH}`}
        height={h}
        width={Math.round(h * VW / VH)}
        style={{ display: 'block', overflow: 'visible' }}
        aria-label="BeeLove.me"
        role="img"
      >
        <defs>
          <linearGradient id={`gd_${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor={goldStart}/>
            <stop offset="60%"  stopColor={goldMid}/>
            <stop offset="100%" stopColor={goldEnd}/>
          </linearGradient>
          {animate && (
            <style>{`
              @keyframes drawEkg_${uid} { to { stroke-dashoffset: 0; } }
              @keyframes dotPop_${uid}  {
                0%  { opacity:0; r:0; }
                60% { opacity:1; r:4; }
                100%{ opacity:1; r:2.5; }
              }
            `}</style>
          )}
        </defs>

        <text x="0" y="40"
          fontFamily="'Cormorant Garamond', Georgia, serif"
          fontWeight="700" fontSize="42"
          fill={textMain}
        >Bee</text>

        <text x="85" y="40"
          fontFamily="'Cormorant Garamond', Georgia, serif"
          fontWeight="300" fontSize="42"
          fill={`url(#gd_${uid})`}
        >Love</text>

        <text x="242" y="30"
          fontFamily="'Courier New', monospace"
          fontSize="15"
          fill={muted}
        >.me</text>

        <path
          d={dCompact}
          fill="none"
          stroke={`url(#gd_${uid})`}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={animStyle}
        />

        <circle
          cx={ptsCompact[ptsCompact.length - 1][0]}
          cy={ptsCompact[ptsCompact.length - 1][1]}
          r="2.5"
          fill={goldSolid}
          style={animate ? {
            opacity: 0,
            animation: `dotPop_${uid} 0.35s ease forwards`,
            animationDelay: '2.1s',
          } : {}}
        />
      </svg>
    );
  }

  // ── Full ──
  const VW = 640, VH = tagline ? 106 : 96;
  const h  = height ?? 72;
  const animStyle = animate ? {
    strokeDasharray:  lenFull,
    strokeDashoffset: lenFull,
    animation: `drawEkgFull_${uid} 2.8s cubic-bezier(.4,0,.2,1) forwards`,
    animationDelay: '0.3s',
  } : { strokeDasharray: 'none' };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VW} ${VH}`}
      height={h}
      width={Math.round(h * VW / VH)}
      style={{ display: 'block', overflow: 'visible' }}
      aria-label="BeeLove.me"
      role="img"
    >
      <defs>
        <linearGradient id={`gdf_${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={goldStart}/>
          <stop offset="60%"  stopColor={goldMid}/>
          <stop offset="100%" stopColor={goldEnd}/>
        </linearGradient>
        <filter id={`glow_${uid}`}>
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        {animate && (
          <style>{`
            @keyframes drawEkgFull_${uid} { to { stroke-dashoffset: 0; } }
            @keyframes dotFull_${uid} {
              0%  { opacity:0; r:0; }
              60% { opacity:1; r:6; }
              100%{ opacity:1; r:3.5; }
            }
            @keyframes fadeTag_${uid} { to { opacity: 1; } }
          `}</style>
        )}
      </defs>

      <text x="0" y="72"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontWeight="700" fontSize="72"
        fill={textMain}
      >Bee</text>

      <text x="146" y="72"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontWeight="300" fontSize="72"
        fill={`url(#gdf_${uid})`}
      >Love</text>

      <text x="496" y="50"
        fontFamily="'Courier New', monospace"
        fontSize="22"
        fill={muted}
      >.me</text>

      <path
        d={dFull}
        fill="none"
        stroke={`url(#gdf_${uid})`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#glow_${uid})`}
        style={animStyle}
      />

      <circle
        cx={ptsFull[ptsFull.length - 1][0]}
        cy={ptsFull[ptsFull.length - 1][1]}
        r="3.5"
        fill={goldSolid}
        style={animate ? {
          opacity: 0,
          animation: `dotFull_${uid} 0.4s ease forwards`,
          animationDelay: '2.9s',
        } : {}}
      />

      {tagline && (
        <text x="1" y="100"
          fontFamily="'Courier New', monospace"
          fontSize="10"
          letterSpacing="3"
          fill={goldSolid}
          opacity={animate ? 0 : 0.55}
          style={animate ? {
            animation: `fadeTag_${uid} 0.6s ease forwards`,
            animationDelay: '2.5s',
          } : {}}
        >{tr('logo.tagline').split('').join(' ')}</text>
      )}
    </svg>
  );
}
